/**
 * Admin Console IA from 07-03 (grouped rail) and 07-02 root destinations.
 * Investigate (007–013) and Actions (014–023) are palette / case / contextual — not root items.
 * Provider configuration children are nested under Providers, never root.
 */
export type AdminNavItem = {
  href: string;
  label: string;
  screenId: string;
  /** Shown when any listed permission is granted. Omit = always shown when authed. */
  anyOf?: readonly string[];
  pinBottom?: boolean;
  /** Nested under another root item (Adyen / Email under Providers). Not a root destination. */
  nestUnderScreenId?: string;
};

export type AdminNavGroupId =
  | 'dashboard'
  | 'cases'
  | 'moderation'
  | 'risk'
  | 'tenants'
  | 'platform'
  | 'reports'
  | 'notifications'
  | 'emergency';

export type AdminNavGroup = {
  id: AdminNavGroupId;
  label: string;
  items: readonly AdminNavItem[];
  pinBottom?: boolean;
};

const DASHBOARD: AdminNavItem = {
  href: '/admin-dashboard',
  label: 'Dashboard',
  screenId: 'SCR-ADM-002',
};

const CASES: readonly AdminNavItem[] = [
  {
    href: '/case-queue',
    label: 'Queue',
    screenId: 'SCR-ADM-003',
    anyOf: ['PERM-support.case.read', 'PERM-support.case.assign', 'PERM-support.case.list'],
  },
  {
    href: '/macro-management',
    label: 'Macros',
    screenId: 'SCR-ADM-006',
    anyOf: ['PERM-support.macro.manage'],
  },
];

const MODERATION: readonly AdminNavItem[] = [
  {
    href: '/moderation-queue',
    label: 'Queue',
    screenId: 'SCR-ADM-024',
    anyOf: ['PERM-moderation.queue.read'],
  },
  {
    href: '/moderation-appeals',
    label: 'Appeals',
    screenId: 'SCR-ADM-027',
    anyOf: ['PERM-moderation.appeal.decide', 'PERM-moderation.appeal.read'],
  },
];

const RISK: readonly AdminNavItem[] = [
  {
    href: '/fraud-queue',
    label: 'Fraud',
    screenId: 'SCR-ADM-028',
    anyOf: ['PERM-fraud.case.read'],
  },
  {
    href: '/risk-blocks',
    label: 'Blocks',
    screenId: 'SCR-ADM-030',
    anyOf: ['PERM-fraud.block.create', 'PERM-fraud.block.remove'],
  },
  {
    href: '/risk-rules',
    label: 'Rules',
    screenId: 'SCR-ADM-031',
    anyOf: ['PERM-fraud.rule.write', 'PERM-fraud.rule.read'],
  },
];

const TENANTS: readonly AdminNavItem[] = [
  {
    href: '/tenant-list',
    label: 'Tenant list',
    screenId: 'SCR-ADM-034',
    anyOf: ['PERM-tenant.business.list', 'PERM-tenant.business.read'],
  },
];

const PLATFORM: readonly AdminNavItem[] = [
  {
    href: '/platform-users-and-roles',
    label: 'Users and roles',
    screenId: 'SCR-ADM-037',
    anyOf: [
      'PERM-authz.assignment.read',
      'PERM-authz.role.assign',
      'PERM-identity.platformrole.grant',
    ],
  },
  {
    href: '/platform-configuration',
    label: 'Configuration',
    screenId: 'SCR-ADM-038',
    anyOf: ['PERM-platform.config.read', 'PERM-platform.config.update'],
  },
  {
    href: '/feature-flags',
    label: 'Feature flags',
    screenId: 'SCR-ADM-039',
    anyOf: ['PERM-featureflag.flag.read', 'PERM-featureflag.flag.enable'],
  },
  {
    href: '/notification-templates',
    label: 'Templates',
    screenId: 'SCR-ADM-040',
    anyOf: [
      'PERM-notification.template.update',
      'PERM-notification.template.publish',
      'PERM-notification.template.read',
    ],
  },
  {
    href: '/broadcast',
    label: 'Broadcast',
    screenId: 'SCR-ADM-041',
    anyOf: ['PERM-notification.broadcast.send'],
  },
  {
    href: '/provider-configuration',
    label: 'Providers',
    screenId: 'SCR-ADM-043',
    anyOf: ['PERM-integration.provider.write', 'PERM-integration.provider.read'],
  },
  {
    href: '/provider-configuration/adyen-credentials',
    label: 'Adyen credentials',
    screenId: 'SCR-ADM-049',
    anyOf: ['PERM-integration.provider.write', 'PERM-integration.provider.read'],
    nestUnderScreenId: 'SCR-ADM-043',
  },
  {
    href: '/beat-pay-operations',
    label: 'BeatQ Pay operations',
    screenId: 'SCR-ADM-053',
    anyOf: ['PERM-integration.provider.write', 'PERM-integration.provider.read'],
    nestUnderScreenId: 'SCR-ADM-043',
  },
  {
    href: '/provider-configuration/email-configurations',
    label: 'Email configurations',
    screenId: 'SCR-ADM-050',
    anyOf: ['PERM-integration.provider.write', 'PERM-integration.provider.read'],
    nestUnderScreenId: 'SCR-ADM-043',
  },
  {
    href: '/provider-configuration/uber-direct-credentials',
    label: 'BeatQ Del credentials',
    screenId: 'SCR-ADM-051',
    anyOf: ['PERM-integration.provider.write', 'PERM-integration.provider.read'],
    nestUnderScreenId: 'SCR-ADM-043',
  },
  {
    href: '/beatq-del',
    label: 'BeatQ Del',
    screenId: 'SCR-ADM-055',
    anyOf: ['PERM-delivery.job.read', 'PERM-platform.config.read'],
    nestUnderScreenId: 'SCR-ADM-038',
  },
  {
    href: '/beatq-del/pricing',
    label: 'BeatQ Del pricing',
    screenId: 'SCR-ADM-056',
    anyOf: ['PERM-platform.config.read'],
    nestUnderScreenId: 'SCR-ADM-038',
  },
  {
    href: '/payout-pricing',
    label: 'Payout pricing',
    screenId: 'SCR-ADM-057',
    anyOf: ['PERM-platform.config.read', 'PERM-pricing.fee.configure'],
    nestUnderScreenId: 'SCR-ADM-038',
  },
  {
    href: '/finance-controls',
    label: 'Finance controls',
    screenId: 'SCR-ADM-058',
    anyOf: [
      'PERM-ledger.correction.approve',
      'PERM-payout.hold.create',
      'PERM-payout.hold.release',
    ],
    nestUnderScreenId: 'SCR-ADM-038',
  },
  {
    href: '/provider-configuration/sms-configurations',
    label: 'SMS configurations',
    screenId: 'SCR-ADM-052',
    anyOf: ['PERM-integration.provider.write', 'PERM-integration.provider.read'],
    nestUnderScreenId: 'SCR-ADM-043',
  },
];

const REPORTS: readonly AdminNavItem[] = [
  {
    href: '/platform-reports',
    label: 'Reports',
    screenId: 'SCR-ADM-044',
    anyOf: [
      'PERM-report.platform.read',
      'PERM-report.financial.read',
      'PERM-report.fraud.read',
      'PERM-report.support.read',
    ],
  },
  {
    href: '/audit-search',
    label: 'Audit',
    screenId: 'SCR-ADM-045',
    anyOf: ['PERM-audit.log.read', 'PERM-audit.log.search'],
  },
];

const NOTIFICATIONS: AdminNavItem = {
  href: '/notification-centre',
  label: 'Notifications',
  screenId: 'SCR-ADM-046',
  anyOf: ['PERM-notification.history.read'],
};

const EMERGENCY: AdminNavItem = {
  href: '/emergency-controls',
  label: 'Emergency',
  screenId: 'SCR-ADM-042',
  anyOf: ['PERM-platform.emergency.pause', 'PERM-platform.emergency.resume'],
  pinBottom: true,
};

/** 07-03 rail groupings. Emergency is pinBottom and always findable. */
export const ADMIN_NAV_GROUPS: readonly AdminNavGroup[] = [
  { id: 'dashboard', label: 'Dashboard', items: [DASHBOARD] },
  { id: 'cases', label: 'Cases', items: CASES },
  { id: 'moderation', label: 'Moderation', items: MODERATION },
  { id: 'risk', label: 'Risk', items: RISK },
  { id: 'tenants', label: 'Tenants', items: TENANTS },
  { id: 'platform', label: 'Platform', items: PLATFORM },
  { id: 'reports', label: 'Reports and Audit', items: REPORTS },
  { id: 'notifications', label: 'Notifications', items: [NOTIFICATIONS] },
  { id: 'emergency', label: 'Emergency', items: [EMERGENCY], pinBottom: true },
];

/** Root destinations only (07-02). Nested 049/050 are excluded. */
export const ADMIN_ROOT_NAV: readonly AdminNavItem[] = ADMIN_NAV_GROUPS.flatMap((group) =>
  group.items.filter((item) => !item.nestUnderScreenId),
);

/** 07-02 root navigation screen ids — Super Admin must see every grouping that contains these. */
export const ADMIN_SUPER_RAIL_REQUIRED_SCREENS = [
  'SCR-ADM-002',
  'SCR-ADM-003',
  'SCR-ADM-006',
  'SCR-ADM-024',
  'SCR-ADM-027',
  'SCR-ADM-028',
  'SCR-ADM-030',
  'SCR-ADM-031',
  'SCR-ADM-034',
  'SCR-ADM-037',
  'SCR-ADM-038',
  'SCR-ADM-039',
  'SCR-ADM-040',
  'SCR-ADM-041',
  'SCR-ADM-043',
  'SCR-ADM-044',
  'SCR-ADM-045',
  'SCR-ADM-046',
  'SCR-ADM-042',
] as const;

export const ADMIN_ROOT_NAV_EXCLUDED_SCREENS = [
  'SCR-ADM-049',
  'SCR-ADM-050',
  'SCR-ADM-051',
  'SCR-ADM-052',
  'SCR-ADM-053',
  'SCR-ADM-054',
  'SCR-ADM-055',
  'SCR-ADM-056',
  'SCR-ADM-057',
  'SCR-ADM-058',
] as const;

export function isSuperAdminOperator(opts: {
  email?: string | null;
  roles?: readonly string[] | null;
}): boolean {
  const email = opts.email?.trim().toLowerCase() ?? '';
  if (email.startsWith('admin@')) return true;
  return Boolean(opts.roles?.includes('ROLE-PLT-001'));
}

export function filterAdminNav(
  items: readonly AdminNavItem[],
  can: (perm: string) => boolean,
  permissionsReady: boolean,
): AdminNavItem[] {
  return items.filter((item) => {
    if (!item.anyOf?.length) return true;
    if (!permissionsReady) return true;
    // Emergency stays findable even when pause/resume are absent (07-03).
    if (item.pinBottom) return true;
    return item.anyOf.some((p) => can(p));
  });
}

export function filterAdminNavGroups(
  groups: readonly AdminNavGroup[],
  can: (perm: string) => boolean,
  permissionsReady: boolean,
  superAdmin: boolean,
): AdminNavGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items:
        superAdmin || !permissionsReady ? [...group.items] : filterAdminNav(group.items, can, true),
    }))
    .filter((group) => {
      if (group.pinBottom) return true;
      if (superAdmin) return true;
      return group.items.some((item) => !item.nestUnderScreenId);
    });
}

export function nestedNavChildren(
  items: readonly AdminNavItem[],
  parentScreenId: string,
): AdminNavItem[] {
  return items.filter((item) => item.nestUnderScreenId === parentScreenId);
}

export function topLevelNavItems(items: readonly AdminNavItem[]): AdminNavItem[] {
  return items.filter((item) => !item.nestUnderScreenId);
}
